import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import "./ColumnFilterPopover.scss";

const TRIGGER_GAP = 6;
const VIEWPORT_MARGIN = 8;

interface FilterPopoverPortalProps {
  children: React.ReactNode;
}

// Renders the column filter popover into document.body, anchored below the
// element it is rendered inside (the filter trigger wrapper). Tables clip their
// overflow for horizontal scrolling, so an absolutely positioned popover would
// be cut off at the table edge.
const FilterPopoverPortal: React.FunctionComponent<
  FilterPopoverPortalProps
> = ({ children }) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({
    visibility: "hidden",
  });

  useLayoutEffect(() => {
    const recompute = () => {
      const anchor = anchorRef.current?.parentElement;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;

      const anchorRect = anchor.getBoundingClientRect();
      const { width, height } = popover.getBoundingClientRect();

      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(anchorRect.left, window.innerWidth - width - VIEWPORT_MARGIN)
      );

      let top = anchorRect.bottom + TRIGGER_GAP;
      if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
        const flippedTop = anchorRect.top - height - TRIGGER_GAP;
        top =
          flippedTop >= VIEWPORT_MARGIN
            ? flippedTop
            : Math.max(
                VIEWPORT_MARGIN,
                window.innerHeight - height - VIEWPORT_MARGIN
              );
      }

      setStyle({ top, left });
    };

    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, []);

  return (
    <>
      <span ref={anchorRef} hidden />
      {createPortal(
        <div
          ref={popoverRef}
          className="column-filter-popover"
          style={style}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>,
        document.body
      )}
    </>
  );
};

export default FilterPopoverPortal;
